import { Injectable } from '@angular/core';
import _ from 'lodash';
import { PageFavorite } from '../models/page-favorite';
import { PageService } from './page.service';
import { CrudService } from './crud.service';
import { Page } from '../models/page';
import { ApplicationStateService } from './application-state.service';
import { PageFavoriteFactory } from '../models/factories/page-favorite-factory';
import { CommunicationService } from './communication.service';
import { BookFeatureDataChangedEvent } from '../utils/events/book-feature-data-changed-event';

@Injectable({
    providedIn: 'root'
})
export class PageFavoriteService {

    private pageFavorites: Array<PageFavorite>;

    constructor(private pageService: PageService,
                private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private crudService: CrudService) {
    }

    setPageFavorites(pageFavorites: Array<PageFavorite>): void {
        this.pageFavorites = [];
        pageFavorites.forEach(element => {

            const pageFavorite = element as PageFavorite;
            pageFavorite.index = this.pageService.getIndexByPageId(pageFavorite.pageId);
            this.pageFavorites.push(pageFavorite);
        });
    }

    /**
     * Similar to setPageFavorites(pageFavorites) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToLinks(objects: Array<any>): void {
        this.pageFavorites = [];
        const pageFavoriteFactory = new PageFavoriteFactory();
        objects.forEach(element => {
            const pageFavorite = pageFavoriteFactory.buildFromObject(element);
            pageFavorite.index = this.pageService.getIndexByPageId(pageFavorite.pageId);
            this.pageFavorites.push(pageFavorite);
        });
    }

    getPageFavorites(): Promise<Array<PageFavorite>> {

        return new Promise<Array<PageFavorite>>(resolve => {

            if (!this.applicationStateService.bookConfig.bookPermissions.pageFavorite.view) {
                this.pageFavorites = [];
            }

            if (this.pageFavorites) {
                resolve(this.pageFavorites);

                return;
            }

            this.crudService.getAllEntitiesOfBook(PageFavorite, new PageFavoriteFactory())
                .then(pageFavorites => {
                    this.setPageFavorites(pageFavorites as Array<PageFavorite>);
                    resolve(pageFavorites as Array<PageFavorite>);
                });
        });
    }

    createPageFavorite(pageFavorite: PageFavorite): Promise<PageFavorite> {

        return new Promise<PageFavorite>(resolve => {

            const canEdit = this.applicationStateService.bookConfig.bookPermissions.pageFavorite.create;

            if (canEdit) {

                this.crudService.create(pageFavorite)
                    .then(savedFavorite => {
                        this.reloadData()
                            .then(result => {
                                this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent
                                    .createEvent(pageFavorite));
                                this.logUsage(pageFavorite);
                                resolve(savedFavorite as PageFavorite);
                            });
                    });

            } else {
                this.pageFavorites.push(pageFavorite);
                this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(pageFavorite));
                resolve(pageFavorite);
            }
        });
    }

    deletePageFavorite(pageFavorite: PageFavorite): Promise<boolean> {

        return new Promise<boolean>(resolve => {
            const canDelete = this.applicationStateService.bookConfig.bookPermissions.pageFavorite.use;
            if (canDelete) {

                this.crudService.remove(pageFavorite)
                    .then(() => {
                        return this.reloadData()
                            .then(() => {
                                this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent
                                    .createEvent(pageFavorite));
                                this.logUsage(pageFavorite);
                                resolve(true);
                            });
                    });

            } else {

                const indexOfFavorite = _.findIndex(this.pageFavorites, { index: pageFavorite.index });
                this.pageFavorites.splice(indexOfFavorite, 1);
                this.communicationService.announceBookFeatureDataChanged(BookFeatureDataChangedEvent.createEvent(pageFavorite));
                resolve(true);
            }
        });
    }

    shouldShowLeftBookmark(page: Page): boolean {
        const isLeftPage = this.pageService.isLeftPage(page);

        return isLeftPage &&
            page.index >= this.applicationStateService.bookConfig.firstPage;
    }

    shouldShowRightBookmark(page: Page): boolean {
        const isRightPage = this.pageService.isRightPage(page);

        return isRightPage &&
            this.applicationStateService.currentViewMode.pagesToShow > 1 &&
            page.index >= this.applicationStateService.bookConfig.firstPage &&
            this.calculateCurrentRightPageIndex() > this.applicationStateService.bookConfig.firstPage;
    }

    logUsage(pageFavorite: PageFavorite): Promise<boolean> {
        return this.crudService.log(pageFavorite);
    }

    private calculateCurrentRightPageIndex(): number {
        return this.applicationStateService.currentIndex + this.applicationStateService.currentViewMode.pagesToShow - 1;
    }

    private reloadData(): Promise<Array<PageFavorite>> {
        this.pageFavorites = undefined;

        return this.getPageFavorites();
    }
}
